import { __rest } from 'tslib';
import React from 'react';
import { styled } from '../../../libs/styled.es.js';
import { Button } from '../../atoms/Button/Button.es.js';
import { SvgIcon } from '../../atoms/SvgIcon/SvgIcon.es.js';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';
const StyledPagination = styled.ul `
  margin-top: 0;
  margin-bottom: 0;
  width: fit-content;
  display: flex;
`;
const StyledPaginationBtn = styled.li(({ theme, disabled, active }) => `
    pointer-events: ${disabled ? 'none' : 'auto'};
    display: inline-block;
    ${active &&
    `color: #fff;
      border-color: ${theme.brand.primary};
      &:hover {
        color: ${theme.brand.primary};
      }`}
  `);
const PageLink = styled(Button)(({ theme, active }) => `
  min-width: 36px;
  height: 36px;
  text-align: center;
  box-shadow: none;
  border-color: #ced4da;
  color: ${theme.brand.primary};
  font-size: 12px;
  background-color: #fff;
  padding: 8px;
  margin: 5px;
  &:hover {
    background-color: #f4f4f4;
  }
  ${active &&
    `color: #fff;
    background-color: ${theme.brand.primary};
    border-color: ${theme.brand.primary};
    border-radius: 4px;
    &:hover {
      color: ${theme.brand.primary};
      background-color: ${theme.brand.primary};
      color: #fff;
      border-color: ${theme.brand.primary};
    }
    `}
`);
const DotItem = styled.li `
  pointer-events: none;
  display: flex;
  min-width: 36px;
  height: 36px;
  color: ${(props) => props.theme.brand.primary};
  padding: 8px;
  margin: 5px;
  font-size: 12px;
  justify-content: center;
`;
const range = (from, to, step = 1) => {
    return Array.from(Array(to - from + 1).keys()).map((item) => item * step + from);
};
const Pagination = (_a) => {
    var { totalRecords = 0, pageLimit = 30, pageNeighbours = 2, onPageChanged, currentPage } = _a, props = __rest(_a, ["totalRecords", "pageLimit", "pageNeighbours", "onPageChanged", "currentPage"]);
    const totalPages = Math.ceil(totalRecords / pageLimit);
    const gotoPage = (page) => {
        onPageChanged(page);
    };
    const handleClick = (page, e) => {
        e.preventDefault();
        gotoPage(page);
    };
    const handleMoveLeft = (e) => {
        e.preventDefault();
        gotoPage(currentPage - 1);
    };
    const handleMoveRight = (e) => {
        e.preventDefault();
        gotoPage(currentPage + 1);
    };
    const fetchPageNumbers = () => {
        const totalNumbers = pageNeighbours * 2 + 3;
        const totalBlocks = totalNumbers + 2;
        if (totalPages > totalBlocks) {
            let pages = [];
            const curr = currentPage > totalPages ? totalPages : currentPage;
            const leftBound = curr - pageNeighbours;
            const rightBound = curr + pageNeighbours;
            const beforeLastPage = totalPages - 1;
            const startPage = leftBound > 2 ? leftBound : 2;
            const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;
            pages = range(startPage, endPage);
            const pagesCount = pages.length;
            const singleSpillOffset = totalNumbers - pagesCount - 1;
            const leftSpill = startPage > 2;
            const rightSpill = endPage < beforeLastPage;
            const leftSpillPage = LEFT_PAGE;
            const rightSpillPage = RIGHT_PAGE;
            if (leftSpill && !rightSpill) {
                const extraPages = range(startPage - singleSpillOffset, startPage - 1);
                pages = [leftSpillPage, ...extraPages, ...pages];
            }
            else if (!leftSpill && rightSpill) {
                const extraPages = range(endPage + 1, endPage + singleSpillOffset);
                pages = [...pages, ...extraPages, rightSpillPage];
            }
            else if (leftSpill && rightSpill) {
                pages = [leftSpillPage, ...pages, rightSpillPage];
            }
            return [1, ...pages, totalPages];
        }
        return range(1, totalPages);
    };
    const pages = fetchPageNumbers();
    return pages.length > 0 ? (React.createElement("nav", Object.assign({}, props),
        React.createElement(StyledPagination, null,
            React.createElement(StyledPaginationBtn, { disabled: currentPage === 1, "data-track-content": true, "data-content-region-name": "paging", "data-content-name": "nextPage" },
                React.createElement(PageLink, { onClick: handleMoveLeft, "aria-label": "Previous page" },
                    React.createElement(SvgIcon, { name: "chevron-back", size: 12 }))),
            pages.map((page, index) => {
                if ([LEFT_PAGE, RIGHT_PAGE].includes(page)) {
                    return React.createElement(DotItem, { key: index }, "...");
                }
                return (React.createElement(StyledPaginationBtn, { key: index, active: currentPage === page, "data-track-content": true, "data-content-region-name": "paging", "data-content-name": "gotoPage", "data-content-target": `${page}` },
                    React.createElement(PageLink, { onClick: (e) => handleClick(page, e), active: currentPage === page, "aria-label": "Next page" }, page)));
            }),
            React.createElement(StyledPaginationBtn, { disabled: currentPage === totalPages, "data-track-content": true, "data-content-region-name": "paging", "data-content-name": "nextPage" },
                React.createElement(PageLink, { onClick: handleMoveRight },
                    React.createElement(SvgIcon, { name: "chevron-forward", size: 12 })))))) : null;
};

export { Pagination };
